import { useHasAccess } from 'lib-shared-access-control';
import { Sentry } from 'lib-shared-sentry';
import { useEffect, useMemo, useState } from 'react';
import { useMount } from 'react-use';

import { dashboardApi } from 'apis/dashboard';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useServerStorage } from 'utils/useServerStorage';

type UseDashboardViewInitResult = {
  isInitialized: boolean;
};

/**
 * A hook that handles initializing the dashboard view based on URL params and
 * other conditions such as persisted user preferences.
 */
export const useDashboardViewInit = (): UseDashboardViewInitResult => {
  const { params } = useAppSearchParams('/dashboard/queues');
  const storage = useServerStorage('user');
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const flags = useFeatureFlag(['dashboard-enhanced-favorites']);
  const hasAnalyticsAccess = useHasAccess('analytics');

  const [isInitialized, setIsInitialized] = useState(false);

  // Handles the case where the queue type in the URL search params is not found.
  // This can happen when accessing the page via a link to the /dashboard/queues path.
  // In this case, we redirect to the default view based on user permissions.
  useMount(() => {
    if (params.type) return;

    navigate(
      {
        path: '/dashboard/queues',
        params: {
          search: {
            type: hasAnalyticsAccess ? 'all' : 'agent',
          },
        },
      },
      { replace: true },
    );
  });

  // Updates the init status when a supported `type` URL search param is found.
  useEffect(() => {
    const initialize = async () => {
      if (params.type && !isInitialized) {
        await initializedAndMigrateFavorites();
        setIsInitialized(true);
      }
    };

    initialize();
    // Only run this effect when the URL param changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.type]);

  // Initializes and migrates the old favorite queues from user-storage to the new favorites API.
  const initializedAndMigrateFavorites = async () => {
    if (!flags.dashboardEnhancedFavorites) return;

    return new Promise<void>(async (resolve, reject) => {
      try {
        const {
          data: { favorites },
        } = await dispatch(
          dashboardApi.endpoints.readDashboardFavorites.initiate(
            {
              dashboard: 'queue',
            },
            { subscribe: true, forceRefetch: true },
          ),
        ).unwrap();

        const { favoriteQueueIds = [] } = storage.data.dashboard ?? {};

        // Migrate the old favorite queues to the new API by creating a default favorite list with the
        // queues, then removing the old data from user-storage. This should only run once per user
        // the first time they access the queue dashboard after the feature flag is enabled.
        if (!favorites.length && favoriteQueueIds.length) {
          const {
            data: { favorite },
          } = await dispatch(
            dashboardApi.endpoints.createDashboardFavorite.initiate({
              favorite: {
                dashboard: 'queue',
                name: 'My favorites',
                params: {
                  queue_ids: favoriteQueueIds,
                  sort_by: 'name:asc',
                },
                shared: false,
              },
            }),
          ).unwrap();

          // Remove the old favorite queues from user-storage to prevent re-running the migration.
          storage.set({
            dashboard: (draft) => {
              delete draft.favoriteQueueIds;

              // Clean up some other deprecated properties while we're at it.
              // @ts-expect-error Deprecated property.
              delete draft.prefersNewDashboard;
              // @ts-expect-error Deprecated property.
              delete draft.isNewDashboardBannerDismissed;
            },
          });

          // If the user accessed the dashboard on the old favorites view, redirect seamlessly to
          // the newly created favorite list.
          if (params.type === 'favourite' && !params.id) {
            navigate(
              {
                path: '/dashboard/queues',
                params: {
                  search: {
                    type: 'favourite',
                    id: favorite.id,
                  },
                },
              },
              { replace: true },
            );
          }
        }

        resolve();
      } catch (error) {
        Sentry.captureException(
          new Error('Dashboard: Failed to initialize and migrate favorites'),
          {
            extra: { error },
          },
        );

        reject(error);
      }
    });
  };

  return useMemo(() => ({ isInitialized }), [isInitialized]);
};
